class PurchaseOrderService {
  #cashQoreAxiosInstance;

  constructor(cashQoreAxiosInstance) {
    if (!cashQoreAxiosInstance) {
      throw new Error('Axios instance is required');
    }
    this.#cashQoreAxiosInstance = cashQoreAxiosInstance;
  }

  /**
 * Searches purchase orders based on provided criteria.
 * @param {object} options 
 * @param {number} [options.limit=25] 
 * @param {number} [options.offset=0] 
 * @param {string} [options.orderBy='DateModified'] 
 * @param {string} [options.order='desc'] 
 * @param {string|null} [options.search=null] 
 * @param {string|null} [options.status=null] 
 * @param {string|null} [options.startDate=null] 
 * @param {string|null} [options.endDate=null]
 * @returns {Promise<{count: number, data:[]}>>}
 */
  searchPurchaseOrders({
    limit = 25,
    offset = 0,
    orderBy = 'DateModified',
    order = 'desc',
    search = null,
    status = null,
    startDate = null,
    endDate = null,
    poNumber = null,
    dispatchId = null,
    memberNumber = null,
    requestedServiceType = null,
    providerId = null,
    providerName = null,
    customerName = null,
    providerCategory = null,
    jobType = null,
    providerContact = null,
  }) {
    return this.#cashQoreAxiosInstance.get('/searchPurchaseOrders', {
      params: {
        limit,
        offset,
        orderBy,
        order,
        search,
        status,
        startDate,
        endDate,
        poNumber,
        dispatchId,
        providerId,
        requestedServiceType,
        providerName,
        memberNumber,
        customerName,
        providerCategory,
        jobType,
        providerContact,
      },
    });
  }

  /**
   * Searches purchase orders for a given dispatch id
   * @param {number} dispatchId
   * @returns {Promise<{data: object[]}>}
   */
  getPurchaseOrdersByDispatchId(dispatchId) {
    return this.#cashQoreAxiosInstance.get(`getPurchaseOrdersByDispatchId/${dispatchId}`);
  }

  /**
 * Retrieves details of a purchase order by its ID.
 * @param {string} purchaseOrderId
 * @returns {Promise<{
 *   PoNumber: string,
  *   DispatchId: string,
  *   DigitalDispatchGuid: string,
  *   ProviderAcceptDate: string|null,
  *   ProviderCancelDate: string|null,
  *   IsGoa: boolean|null,
  *   Eta: number,
  *   EstimatedPrice: number|null,
  *   ProviderName: string|null,
  *   DispatcherName: string|null,
  *   TowMagicJobId: string|null,
  *   IncidentLocationId: number,
  *   DropoffLocationId: number,
  *   ProviderRemarks: string|null,
  *   DispatcherPhone: string|null,
  *   EtaConditions: string|null,
  *   MilesToVehicle: number|null,
  *   ProviderId: number|null,
  *   MilesLoaded: number|null,
  *   SpecialInstructions: string|null,
  *   CancelRemarks: string|null,
  *   DispatchReason: string,
  *   RequestedServiceType: string,
  *   VehicleInformationId: number,
  *   CustomerName: string,
  *   CustomerPhone: string,
  *   IsCashCall: boolean,
  *   AmountOwed: number,
  *   JobType: string,
  *   RideAlongCount: number|null,
  *   DateCreated: string,
  *   DateModified: string,
  *   ClientPickupTime: string,
  *   PickupLocationId: number|null,
  *   DestinationLocationId: number|null,
  *   DigitalProviderNetwork: string,
  *   TotalBaseRate: number,
  *   ServiceRate: number,
  *   LoadedMileageRate: number,
  *   FreeLoadedMiles: number,
  *   UnloadedMileageRate: number,
  *   FreeUnloadedMiles: number,
  *   AdditionalRate: number,
  *   SurchargeRate: number,
  *   GoaRate: number,
  *   IncludedDents: number,
  *   AdditionalDentRate: number,
  *   ProviderEmail: string|null,
  *   ProviderPhone: string|null,
  *   ProviderHoursOfOperation: string|null,
  *   ProviderAddress: string|null,
  *   ProviderCity: string|null,
  *   ProviderState: string|null,
  *   ProviderCategory: string,
  *   OneServicePerHours: number|null,
  *   ServicesPerYear: number|null,
  *   ServiceAlways: boolean|null,
  *   MaxCoverageMiles: number|null,
  *   MaxCoverageCosts: number|null,
  *   PreferredLocationMaxSearchMiles: number,
  *   MemberNumber: string|null,
  *   MemberAmountOwed: number|null,
  *   MotorClubAmountOwed: number|null,
  *   MilesUnloaded: number|null,
  *   TwoWayTowReturnLocationId: number|null,
  *   TwoWayVehicleId: number|null,
  *   PriorServiceAmount: number|null,
  *   StorageAmount: number|null,
  *   MaterialAmount: number|null,
  *   Dents: number|null,
  *   CleanUpAmount: number|null,
  *   TaxAmount: number|null,
  *   InternalNotesToProvider: string|null,
  *   ProviderServicePointLatitude: number|null,
  *   ProviderServicePointLongitude: number|null,
  *   IncidentLocationHeadingDirection: string|null,
  *   IncidentLocationMileMarkers: string|null,
  *   IncidentLocationExitNumber: string|null,
  *   IncidentLocationRoadShoulder: string|null,
  *   IncidentLocationLandmarks: string|null,
  *   IncidentLocationCrossStreets: string|null,
  *   IncidentLocationBusinessPhone: string|null,
  *   DestinationLocationBusinessPhone: string|null,
  *   DestinationLocationComments: string|null,
  *   POPaymentAmount: number|null,
  *   InternalNotes: string|null,
  *   ProviderNotes: string|null,
  *   DispatchCreated: string|null,
  *   DispatchState: string|null,
  *   DispatchStatus: string|null
  * }>}
 */
  getPurchaseOrderDetails(purchaseOrderId) {
    return this.#cashQoreAxiosInstance.get('/getPurchaseOrderDetails', {
      params: {
        purchaseOrderId,
      },
    }
    );
  }

  /**
   * Retrieves Charge Types for Purchase Orders.
   * @returns 
   */
  getPurchaseOrderChargeTypes() {
    return this.#cashQoreAxiosInstance.get('/purchase-order/charge-type');
  }

  /**
   * Searches purchase orders history for a given poNumber
   * @param {purchaseOrderId} dispatchId
   * @returns {Promise<{data: object[]}>}
   */
  getPurchaseOrderHistory(purchaseOrderId) {
    return this.#cashQoreAxiosInstance.get(`/getPurchaseOrderHistory?purchaseOrderId=${purchaseOrderId}`);
  }

  /**
   * Retrieves details of a purchase order by its ID.
   * @param {string} poNumber
   * @param {any} Charges
   */
  updatePurchaseOrder(
    poNumber,
    milesUnloaded,
    freeUnloadedMiles,
    unloadedMileageRate,
    milesLoaded,
    freeLoadedMiles,
    loadedMileageRate,
    storageDays,
    storageRate,
    secondaryStorageDays,
    secondaryStorageDailyRate,
    creditCardFeePercent = null,
    charges) {

    return this.#cashQoreAxiosInstance.put('/updatePurchaseOrder', {
      poNumber: poNumber,
      milesUnloaded: milesUnloaded,
      freeUnloadedMiles: freeUnloadedMiles,
      unloadedMileageRate: unloadedMileageRate,
      milesLoaded: milesLoaded,
      freeLoadedMiles: freeLoadedMiles,
      loadedMileageRate: loadedMileageRate,
      storageDays: storageDays,
      storageDailyRate: storageRate,
      secondaryStorageDays: secondaryStorageDays,
      secondaryStorageDailyRate: secondaryStorageDailyRate,
      creditCardFeePercent: creditCardFeePercent,
      Charges: charges,
    });
  }

  /**
   * Searches purchase order attachments for a given poNumber
   * @param {purchaseOrderId} dispatchId
   * @returns {Promise<{data: object[]}>}
   */
  getPurchaseOrderAttachments(purchaseOrderId) {
    return this.#cashQoreAxiosInstance.get(`/getPurchaseOrderAttachments?purchaseOrderId=${purchaseOrderId}`);
  }

  /**
   * Gets the retail payment by the transaction id.
   * 
   * @param {number} transactionId The primary identifier for the retail payment.
   * @returns {Promise<{
   *  data: { 
   *    data: {
   *     PoNumber: string,
   *     RetailPaymentId: string,
   *     DateCreated: string,
   *     Caller: {
   *        FirstName: string,
   *        LastName: string,
   *        Email: string
   *     },
   *     StripePaymentId: string,
   *     StripeInvoiceId: string,
   *     StripeDisputeId: string,
   *     DisputeDate: string,
   *     PaymentDetail:{
   *        SubTotal: number,
   *        Tax: number,
   *        Total: number,
   *       Currency: string
   *     },
   *     Refunds: Array<{
   *        RetailPaymentRefundId: string,
   *        RetailPaymentId: string,
   *        StripeRefundId: string,
   *        RefundAmount: number,
   *        RefundReasonId: number,
   *        RefundReason: string,
   *        RefundUser: string,
   *        RefundNote: string,
   *        IsFullRefund: boolean,
   *        TransactionId: string
   *      }>
   *     }
   *   }
   *  }
   * }>} A promise that resolves with the response data of the list of retail payment purchase orders and the unpaginated total.
   */
  getRetailPaymentDetails(transactionId) {
    return this.#cashQoreAxiosInstance.get('/getPurchaseOrderRetailPaymentDetails', {
      params: {
        transactionId,
      },
    });
  }

  /**
   * Retrieves retail payment details of a purchase order by its Id/number.
   * @param {purchaseOrderId} poNumber
   * @returns {Promise<{data: object[]}>}
   */
  getPurchaseOrderRetailPayments(poNumber) {
    return this.#cashQoreAxiosInstance.get(`/getPurchaseOrderRetailPayments?poNumber=${poNumber}`);
  }

  /**
   * loads purchase order reasons
   * @param {string} category
   * @returns {Promise<{data: string[]}>}
   */
  getPurchaseOrderReasons(category) {
    return this.#cashQoreAxiosInstance.get(`/getPurchaseOrderReasons?category=${category}`);
  }

  /**
   * Creates a supplemental adjustment against an existing purchase order.
   * An adjustment is a child record of the purchase order, not a new purchase order.
   * @param {number} purchaseOrderId
   * @param {string} source either 'internal' or 'external'
   * @param {string|null} notes free text, 500 characters maximum
   * @param {Array<{chargeTypeId: number, amount: number}>} charges every amount must exceed zero
   * @returns {Promise<{data: object}>} the created adjustment record
   */
  createPurchaseOrderAdjustment(purchaseOrderId, source, notes, charges) {
    return this.#cashQoreAxiosInstance.post('/createPurchaseOrderAdjustment', {
      purchaseOrderId: purchaseOrderId,
      source: source,
      notes: notes,
      charges: charges,
    });
  }

  /**
   * Retrieves the adjustments raised against a purchase order and their charges.
   * Charges are returned flat; each one carries the PurchaseOrderAdjustmentId it belongs to.
   * @param {number|string} purchaseOrderId
   * @returns {Promise<{data: {
   *   adjustments: Array<{
   *     PurchaseOrderAdjustmentId: number,
   *     PurchaseOrderId: number,
   *     Type: string,
   *     StatusId: number,
   *     StatusDescription: string,
   *     Source: string,
   *     TotalAmount: number,
   *     Notes: ?string,
   *     CreatedBy: string,
   *     DateCreated: string,
   *     ReviewedBy: ?string,
   *     DateReviewed: ?string
   *   }>,
   *   charges: Array<{
   *     PurchaseOrderAdjustmentId: number,
   *     PurchaseOrderAdjustmentChargeId: number,
   *     ChargeTypeId: number,
   *     PurchaseOrderChargeTypeName: string,
   *     CategoryId: number,
   *     Amount: number,
   *     Quantity: ?number
   *   }>
   * }}>}
   */
  getPurchaseOrderAdjustments(purchaseOrderId) {
    return this.#cashQoreAxiosInstance.get('/getPurchaseOrderAdjustments', {
      params: {
        purchaseOrderId,
      },
    });
  }

  /**
   * Uploads the attachment for a given purchase order ID.
   * @param {number} purchaseOrderId
   * @param {object} file
   * @returns {Promise<{data: object[]}>}
   */
  async uploadPurchaseOrderAttachments(purchaseOrderId, attachment) {
    const formData = new FormData();

    // Update the formData object
    formData.append(
      'file',
      attachment,
      attachment.name
    );

    formData.append('poNumber', purchaseOrderId);

    const response = await this.#cashQoreAxiosInstance.post('/uploadPurchaseOrderAttachment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }
}

export default PurchaseOrderService;

